import { useMemo, useState } from 'react';
import { useAuth } from '@/lib/auth';
import { useViewMode } from '@/lib/view-mode-context';
import { calculatePlayerStats } from '@/lib/player-stats';
import { calculateExpenseNets } from '@/lib/settlement';
import { useUserSettings } from '@/lib/user-settings-context';
import { UiStrategy } from "@/ui/UiStrategy";
import type { SessionWithPlayers, PlayerLeaderboardEntry } from "@/ui/view-props";
import { DashboardClassicView } from "@/ui/v1/DashboardClassicView";
import { DashboardModernView } from "@/ui/v2/DashboardModernView";
import { DashboardNeonView } from "@/ui/v3/DashboardNeonView";
import { useToast } from '@/hooks/use-toast';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { useSessions } from '@/hooks/use-sessions';
import { usePlayers } from '@/hooks/use-players';
import { useExpenses } from '@/hooks/use-expenses';
import { useAllSessionPlayers } from '@/hooks/use-session-players';

export default function Dashboard() {
  const { user } = useAuth();
  const { targetUserId, isReadOnly: isReadOnlyFromContext } = useViewMode();
  const { settings, updateSettings } = useUserSettings();
  const { toast } = useToast();
  const [showAbortConfirm, setShowAbortConfirm] = useState(false);

  const { data: sessions = [], isLoading: sessionsLoading } = useSessions(targetUserId);
  const { data: players = [], isLoading: playersLoading } = usePlayers(targetUserId);
  const { data: expenses = [], isLoading: expensesLoading } = useExpenses(targetUserId);
  const { data: allSessionPlayers = [], isLoading: spLoading } = useAllSessionPlayers(targetUserId);

  const loading = sessionsLoading || playersLoading || expensesLoading || spLoading;

  const dashboardData = useMemo(() => {
    if (loading) return null;

    const unsettled = sessions.filter((s) => s.status === 'unsettled');
    const settled = sessions.filter((s) => s.status === 'settled');

    const stats = {
      unsettled: unsettled.length,
      players: players.length,
      totalSessions: sessions.length,
    };

    let recentSettledSession: SessionWithPlayers | null = null;
    if (settled.length > 0) {
      const lastSettled = [...settled].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())[0];
      const sessionPlayers = allSessionPlayers.filter(sp => sp.session_id === lastSettled.id);
      
      const sessionPlayersData = sessionPlayers.map((sp: any) => ({
        name: sp.players?.name || 'Unknown',
        buyIns: sp.buy_ins || 0,
        net: sp.net_amount || 0,
        cashOut: sp.cash_out || 0,
      }));

      recentSettledSession = {
        id: lastSettled.id,
        date: lastSettled.date,
        status: lastSettled.status,
        buyInUnit: lastSettled.buy_in_unit ?? 20,
        players: sessionPlayersData,
      };
    }

    const expenseMap = calculateExpenseNets(expenses);

    const allTimeStats: PlayerLeaderboardEntry[] = players.map(player => {
      const playerSessions = allSessionPlayers
        .filter(sp => sp.player_id === player.id)
        .map(sp => ({
          net: sp.net_amount || 0,
          buyIns: sp.buy_ins,
          buyInUnit: (sp.sessions as any)?.buy_in_unit,
          date: (sp.sessions as any)?.date
        }))
        .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
      
      return {
        ...calculatePlayerStats(playerSessions, expenseMap[player.id] || 0),
        id: player.id,
        name: player.name,
        nickname: player.nickname,
        avatar_url: player.avatar_url,
      };
    }).sort((a, b) => b.totalNet - a.totalNet);

    const unsettledExpenseMap = calculateExpenseNets(expenses.filter(exp => !exp.is_settled));

    const unsettledStats: PlayerLeaderboardEntry[] = players.map(player => {
      const playerUnsettledSessions = allSessionPlayers
        .filter(sp => sp.player_id === player.id && (sp.sessions as any)?.status === 'unsettled')
        .map(sp => ({
          net: sp.net_amount || 0,
          buyIns: sp.buy_ins,
          buyInUnit: (sp.sessions as any)?.buy_in_unit,
          date: (sp.sessions as any)?.date
        }))
        .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
      
      return {
        ...calculatePlayerStats(playerUnsettledSessions, unsettledExpenseMap[player.id] || 0),
        id: player.id,
        name: player.name,
        nickname: player.nickname,
        avatar_url: player.avatar_url,
      };
    }).filter(s => s.totalSessions > 0 || s.expenseNet !== 0)
      .sort((a, b) => b.totalNet - a.totalNet);

    const unsettledExpenses = expenses.filter(exp => !exp.is_settled);

    let hotPlayers: { name: string; streak: number }[] = [];
    if (settings.whos_hot) {
      const playerSessions: Record<string, { net: number; buyIns: number; buyInUnit: number; date: string }[]> = {};
      allSessionPlayers.forEach((sp: any) => {
        const name = sp.players?.name;
        if (name) {
          if (!playerSessions[name]) playerSessions[name] = [];
          playerSessions[name].push({
            net: sp.net_amount || 0,
            buyIns: sp.buy_ins,
            buyInUnit: sp.sessions?.buy_in_unit || 20,
            date: sp.sessions?.date || '',
          });
        }
      });

      hotPlayers = Object.entries(playerSessions)
        .map(([name, sessions]) => {
          const sortedSessions = [...sessions].sort(
            (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
          );
          const stats = calculatePlayerStats(sortedSessions);
          return { name, streak: stats.currentStreak, streakType: stats.streakType };
        })
        .filter((p) => p.streakType === 'win' && p.streak >= 2)
        .sort((a, b) => b.streak - a.streak);
    }

    return {
      stats,
      recentSettledSession,
      hotPlayers,
      allTimeStats,
      unsettledStats,
      unsettledExpenses
    };
  }, [loading, sessions, players, expenses, allSessionPlayers, settings.whos_hot]);

  const handleAbortSession = async () => {
    try {
      await updateSettings({ active_session_id: null });
      toast({ title: 'Session aborted' });
    } catch {
      toast({ variant: 'destructive', title: 'Failed to abort session' });
    }
  };

  const props = {
    stats: dashboardData?.stats || { unsettled: 0, players: 0, totalSessions: 0 },
    recentSettledSession: dashboardData?.recentSettledSession || null,
    hotPlayers: dashboardData?.hotPlayers || [],
    allTimeStats: dashboardData?.allTimeStats || [],
    unsettledStats: dashboardData?.unsettledStats || [],
    unsettledExpenses: dashboardData?.unsettledExpenses || [],
    loading: loading && sessions.length === 0,
    settings,
    isReadOnly: isReadOnlyFromContext,
    targetUserId,
    activeSessionId: settings.active_session_id,
    onAbortSession: () => setShowAbortConfirm(true),
  };

  return (
    <>
      <UiStrategy
        classic={<DashboardClassicView {...props} />}
        modern={<DashboardModernView {...props} />}
        neon={<DashboardNeonView {...props} />}
      />
      <ConfirmDialog
        open={showAbortConfirm}
        onOpenChange={setShowAbortConfirm}
        title="Abort Session"
        description="Are you sure you want to abort the current session? All progress will be lost."
        confirmLabel="Abort Session"
        onConfirm={handleAbortSession}
      />
    </>
  );
}
